import { queryOptions, type QueryClient } from "@tanstack/react-query";
import type { WorkItemService } from "@/services/work-item.service";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";
import { workItemRelationsPrefix } from "./work-item-relations";

/**
 * A work item's dependencies and links (NG-2806), the reads behind its
 * Relations panel, the Quick View's dependencies table and the links under its
 * attachments. Relations are project-scoped: a project-less item (an app-managed
 * event) has none. Links are not: a project-less item reads its links from the
 * workspace-scoped route (NG-3443).
 *
 * - **Relations**, `[slug, uid, 'work-item-relations', 'item', id]`: the
 *   item's `blocked_by`, `blocks` and `relates_to` peers. It shares the
 *   `'work-item-relations'` family with the Gantt's project edges (`'project'`,
 *   NG-2812), and every relation write on either side re-reads the whole
 *   family, so an edge drawn on the Gantt shows in the item's panel and one
 *   added in the panel redraws an open Gantt.
 * - **Links**, `[slug, uid, 'work-item-links', id]`: the URLs on the item. The
 *   Gantt draws none, so they keep their own family.
 *
 * Mutable, without the visible poll: the store these replace never polled, and
 * a focus or a revisit re-reads them once stale. The project only picks the
 * route, so it is not in the key.
 *
 * @writers `useWorkItemRelations`: a relation add or remove re-reads the
 * relations family (`rereadWorkItemRelations`); a link add or remove writes the
 * confirmed change into the links list, cancelling a read still in flight.
 * `IssueStore.updateIssueDates` also re-reads relations after a bulk date save:
 * each peer carries dates independently of the work-item row cache (NG-3553).
 */

type RelationsReader = Pick<WorkItemService, "listRelations">;
type LinksReader = Pick<WorkItemService, "listLinks">;

const POLICY = { ...FRESHNESS.mutable, refetchInterval: false } as const;

export function workItemRelationsKey(scope: QueryScope, issueId: string) {
  return scopedKey(scope, "work-item-relations", "item", issueId);
}

export function workItemLinksKey(scope: QueryScope, issueId: string) {
  return scopedKey(scope, "work-item-links", issueId);
}

/** A work item's dependency peers, by kind. */
export function workItemRelationsQuery(
  service: RelationsReader,
  scope: QueryScope,
  projectId: string,
  issueId: string,
) {
  return queryOptions({
    ...POLICY,
    queryKey: workItemRelationsKey(scope, issueId),
    queryFn: scopedRead(
      (request) => service.listRelations(scope.tenant, projectId, issueId, request),
      { retry: "safe-read" },
    ),
  });
}

/** A work item's links, oldest first as the API orders them. */
export function workItemLinksQuery(
  service: LinksReader,
  scope: QueryScope,
  projectId: string | null | undefined,
  issueId: string,
) {
  return queryOptions({
    ...POLICY,
    queryKey: workItemLinksKey(scope, issueId),
    queryFn: scopedRead((request) => service.listLinks(scope.tenant, projectId, issueId, request), {
      retry: "safe-read",
    }),
  });
}

/**
 * After a relation or bulk date write: re-read every relations entry in `scope`,
 * the open ones at once. Settles when they have; a failed re-read is the cache's to log.
 */
export function rereadWorkItemRelations(client: QueryClient, scope: QueryScope): Promise<void> {
  return client.invalidateQueries({ queryKey: workItemRelationsPrefix(scope) });
}
